/**
 * map-fill-theme.ts — Temas de relleno del mapa (uno a la vez). El borde
 * siempre marca el cultivo y no depende del tema. Cada tema es una función
 * pura; para agregar otro basta sumar el valor a `FillTheme`, a `FILL_THEMES`
 * y su rama en `fillColorForTheme`/`fillThemeLegend`.
 */
import { ndviMeanToColor, ndviMeanToColorScaled, type NdviRange } from './ndvi-color';

export type FillTheme = 'status' | 'ndvi' | 'stage' | 'custom';

export const FILL_THEMES: FillTheme[] = ['status', 'ndvi', 'stage', 'custom'];
export const DEFAULT_FILL_THEME: FillTheme = 'status';

/** Etapas fenológicas (mismo catálogo que el backend, `crop.model.ts`). */
export const PHENOLOGY_STAGES = [
  'PREPARACION_SUELO', 'SIEMBRA', 'GERMINACION', 'EMERGENCIA', 'ESTABLECIMIENTO',
  'VEGETATIVO', 'FLORACION', 'FRUCTIFICACION', 'MADURACION', 'COSECHA', 'POST_COSECHA',
] as const;
export type PhenologyStageCode = typeof PHENOLOGY_STAGES[number];

/**
 * Un color por etapa, recorriendo el ciclo (suelo → verdes → floración →
 * fruto → cosecha). Todos con contraste >= 3:1 contra blanco (gráficos, WCAG
 * 1.4.11) para verse tanto sobre calles como sobre satélite; los tonos
 * vecinos difieren en matiz, no solo en luminosidad.
 */
export const STAGE_COLORS: Record<PhenologyStageCode, string> = {
  PREPARACION_SUELO: '#6d4c41',
  SIEMBRA:           '#a65f00',
  GERMINACION:       '#558b2f',
  EMERGENCIA:        '#2e7d32',
  ESTABLECIMIENTO:   '#00796b',
  VEGETATIVO:        '#0277bd',
  FLORACION:         '#c2185b',
  FRUCTIFICACION:    '#6a1b9a',
  MADURACION:        '#d84315',
  COSECHA:           '#283593',
  POST_COSECHA:      '#546e7a',
};
/** Sin registro fenológico. */
export const STAGE_NO_DATA_COLOR = '#757575';

export function isPhenologyStage(v: unknown): v is PhenologyStageCode {
  return typeof v === 'string' && (PHENOLOGY_STAGES as readonly string[]).includes(v);
}

export function stageToColor(stage?: string | null): string {
  return isPhenologyStage(stage) ? STAGE_COLORS[stage] : STAGE_NO_DATA_COLOR;
}

export interface FillThemeInput {
  ndviMean?: number | null;
  /** Etapa fenológica vigente (la de su lote; las zonas heredan la del lote). */
  stage?: string | null;
  /** Color elegido por el usuario para el lote/zona (#rrggbb). */
  displayColor?: string | null;
  /** Rango de la escala dinámica del NDVI; `null`/ausente = escala fija. */
  ndviRange?: NdviRange | null;
}

/** Color de relleno; `undefined` = el mapa usa el color por estado de la zona. */
export function fillColorForTheme(theme: FillTheme, input: FillThemeInput): string | undefined {
  switch (theme) {
    case 'ndvi': return ndviMeanToColorScaled(input.ndviMean, input.ndviRange ?? null);
    case 'stage': return stageToColor(input.stage);
    case 'custom': return /^#[0-9a-fA-F]{6}$/.test(input.displayColor ?? '') ? input.displayColor! : undefined;
    case 'status':
    default: return undefined;
  }
}

/**
 * `ns` indica en qué namespace de i18n vive la etiqueta: 'map' (claves
 * ndvi*) o 'crop' (clave `stage<CÓDIGO>`).
 */
export interface LegendEntry { color: string; ns: 'map' | 'crop'; key: string; params?: Record<string, string> }

/** Leyenda de los temas que no son "status" (la de estado vive con STATUS_COLORS). */
export function fillThemeLegend(theme: FillTheme, ndviRange?: NdviRange | null): LegendEntry[] | null {
  if (theme === 'ndvi' && ndviRange) {
    // Escala dinámica: extremos reales del rango observado.
    return [
      { color: ndviMeanToColorScaled(ndviRange.min, ndviRange), ns: 'map', key: 'ndviRangeMin', params: { min: ndviRange.min.toFixed(2) } },
      { color: ndviMeanToColorScaled(ndviRange.max, ndviRange), ns: 'map', key: 'ndviRangeMax', params: { max: ndviRange.max.toFixed(2) } },
      { color: ndviMeanToColor(null), ns: 'map', key: 'ndviNoData' },
    ];
  }
  if (theme === 'ndvi') {
    return [
      { color: ndviMeanToColor(0.6), ns: 'map', key: 'ndviHigh' },
      { color: ndviMeanToColor(0.4), ns: 'map', key: 'ndviMid' },
      { color: ndviMeanToColor(0.1), ns: 'map', key: 'ndviLow' },
      { color: ndviMeanToColor(null), ns: 'map', key: 'ndviNoData' },
    ];
  }
  if (theme === 'stage') {
    return [
      ...PHENOLOGY_STAGES.map(s => ({ color: STAGE_COLORS[s], ns: 'crop' as const, key: `stage${s}` })),
      { color: STAGE_NO_DATA_COLOR, ns: 'map', key: 'stageNoData' },
    ];
  }
  return null;
}

export function isFillTheme(v: string): v is FillTheme {
  return (FILL_THEMES as string[]).includes(v);
}
